import React, { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Alert,
    Divider,
    LinearProgress,
} from '@mui/material';
import useFetch from '../../../../../hooks/useFetch';
import RTL from '../../../../../utils/RTL';
import { toPersianDigits } from '../../../../../utils/convertNumbers';
import { formatJalaliDate } from '../../../../../utils/formatJalaliDate';
import { API } from '../../../../../utils/apiRoutes';
import PersianDatePicker from '../../../../common/PersianDatePicker';
import FinancialOverviewCards from '../../../../common/FinancialOverviewCards';
import { PRESET_OPTIONS, calculateDatesFromPreset, toApiDateString } from '../../../../../utils/dateRangePresets';

const PAYMENT_METHOD_LABELS = {
    1: 'نقدی',
    2: 'کارت خوان',
    3: 'انتقال به حساب',
    4: 'سایر',
};

const formatCurrency = (amount) =>
    `${toPersianDigits(new Intl.NumberFormat('fa-IR').format(amount || 0))} تومان`;

const formatPercent = (part, whole) =>
    `${toPersianDigits(whole > 0 ? Math.round((part / whole) * 100) : 0)}٪`;

function RevenueOverview() {
    const DEFAULT_PRESET = 'this_month';
    const initialDates = useMemo(() => calculateDatesFromPreset(DEFAULT_PRESET), []);
    const [preset, setPreset] = useState(DEFAULT_PRESET);
    const [startDate, setStartDate] = useState(initialDates.start);
    const [endDate, setEndDate] = useState(initialDates.end);

    // Always send explicit start/end dates (preset just pre-fills the pickers),
    // so the period shown matches exactly what the user sees in the date fields.
    const apiParams = useMemo(() => {
        const params = {};
        const start = toApiDateString(startDate);
        const end = toApiDateString(endDate);
        if (start) params.start_date = start;
        if (end) params.end_date = end;
        return params;
    }, [startDate, endDate]);

    const { data, loading, error } = useFetch(API.revenueOverview, { params: apiParams });

    // Picking a preset fills both date fields; "custom" leaves them as-is.
    const handlePresetChange = (event) => {
        const newPreset = event.target.value;
        setPreset(newPreset);
        if (newPreset !== 'custom') {
            const { start, end } = calculateDatesFromPreset(newPreset);
            setStartDate(start);
            setEndDate(end);
        }
    };

    const handleStartDateChange = (date) => {
        if (date && !isNaN(date.getTime())) {
            setStartDate(date);
            setPreset('custom');
        }
    };

    const handleEndDateChange = (date) => {
        if (date && !isNaN(date.getTime())) {
            setEndDate(date);
            setPreset('custom');
        }
    };

    return (
        <RTL>
            <Box sx={{ p: 3 }}>
                <Typography variant="h4" sx={{ mb: 3, textAlign: 'center', fontWeight: 'bold' }}>
                    گزارش مالی - درآمدها
                </Typography>

                {/* Filters */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Grid 
                        container 
                        spacing={0}
                        sx={{
                            '& .MuiTextField-root': { m: 0 },
                            '& .MuiFormControl-root': { m: 0 },
                        }}
                    >
                        <Grid item xs={12} sm={6} md={3} sx={{ px: 2, pb: 2 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>دوره زمانی</InputLabel>
                                <Select
                                    value={preset}
                                    label="دوره زمانی"
                                    onChange={handlePresetChange}
                                >
                                    {PRESET_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                    <MenuItem value="custom">انتخاب دستی</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3} sx={{ px: 2, pb: 2 }}>
                            <PersianDatePicker
                                label="از تاریخ"
                                value={startDate ? new Date(startDate) : null}
                                onChange={handleStartDateChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} sx={{ px: 2, pb: 2 }}>
                            <PersianDatePicker
                                label="تا تاریخ"
                                value={endDate ? new Date(endDate) : null}
                                onChange={handleEndDateChange}
                            />
                        </Grid>
                    </Grid>
                </Paper>

                {/* Error Display */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {/* Loading State */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                )}

                {/* KPI Cards */}
                {data && !loading && (
                    <>
                        <Box sx={{ mb: 3 }}>
                            <FinancialOverviewCards data={data} />
                        </Box>

                        {/* Cash collected at registration, broken down by method.
                            This is a cash-in figure (registration operations only)
                            and is intentionally distinct from recognized درآمد. */}
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                مبلغ دریافتی بر اساس روش پرداخت
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                وجوه دریافتی هنگام ثبت‌نام — متفاوت با درآمد شناسایی‌شده
                            </Typography>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                                مجموع دریافتی: {formatCurrency(
                                    Object.values(data.payment_method_totals || {}).reduce((a, b) => a + (b || 0), 0)
                                )}
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Grid container spacing={2}>
                                {(() => {
                                    const totals = data.payment_method_totals || {};
                                    const grandTotal = Object.values(totals).reduce((a, b) => a + (b || 0), 0);
                                    return Object.entries(totals).map(([method, total]) => (
                                        <Grid item xs={12} sm={6} md={3} key={method}>
                                            <Box
                                                sx={{
                                                    p: 2,
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    borderRadius: 1,
                                                    textAlign: 'center',
                                                }}
                                            >
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                    {PAYMENT_METHOD_LABELS[method] || 'نامشخص'}
                                                </Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                    {formatCurrency(total)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatPercent(total || 0, grandTotal)}
                                                </Typography>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={grandTotal > 0 ? Math.round(((total || 0) / grandTotal) * 100) : 0}
                                                    sx={{ mt: 1, borderRadius: 1, height: 6 }}
                                                />
                                            </Box>
                                        </Grid>
                                    ));
                                })()}
                            </Grid>
                        </Paper>

                        {/* Period Info */}
                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                گزارش از {toPersianDigits(formatJalaliDate(data.period_start))} تا {toPersianDigits(formatJalaliDate(data.period_end))}
                            </Typography>
                        </Box>
                    </>
                )}
            </Box>
        </RTL>
    );
}

export default RevenueOverview;

